import { Typeahead, type Key } from "@zapier/design-system-beta";
import { Icon, Text } from "@zapier/design-system";
import { useAtomValue, useSetAtom } from "jotai";
import {
  SyntheticEvent,
  KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import {
  Actor,
  actorsAtomFamily,
  floatingToolbarActiveButtonAtom,
  takeSnapshotAtom,
} from "@zapier/canvas-state";
import styles from "./FloatingActorTypeahead.module.css";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { generateActorColor } from "../../../utils/generateActorColor";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { ACTOR_TYPEAHEAD_LABEL } from "@zapier/canvas-constants";
import { WrapperWithBackButton } from "../WrapperWIthBackButton";
import { SelectedItem } from "../SelectedItem";

type Props = {
  actors: Actor[] | undefined;
};

export function FloatingActorTypeahead({ actors }: Props) {
  const allActors = useAtomValue(actorsAtomFamily(false));
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const actor = actors?.[0] ?? null;
  const hasActor = actors && actors.length > 0;

  useFocusInput(inputRef);

  const setActiveButton = useSetAtom(floatingToolbarActiveButtonAtom);

  const returnToMainToolbar = useCallback(
    () => setActiveButton(null),
    [setActiveButton],
  );

  const onInputChange = useCallback(
    (value: string) => setSearchTerm(value),
    [setSearchTerm],
  );

  const onKeyDown = useCallback(
    (event: SyntheticEvent) => {
      const keyboardEvent = event as KeyboardEvent<HTMLInputElement>;

      if (keyboardEvent.key === "Enter") {
        const trimmedSearchTerm = searchTerm.trim();
        if (trimmedSearchTerm) {
          takeSnapshot();
          const existingActorInTypeahead = allActors.find(
            (actor) => actor.label === trimmedSearchTerm,
          );

          updateNodeAndSave({
            data: {
              actors: [
                existingActorInTypeahead ?? {
                  label: trimmedSearchTerm,
                  color: actor?.color ?? generateActorColor(trimmedSearchTerm),
                },
              ],
            },
          });

          setSearchTerm("");
          returnToMainToolbar();
        }
      }
    },
    [
      returnToMainToolbar,
      actor?.color,
      allActors,
      searchTerm,
      takeSnapshot,
      updateNodeAndSave,
    ],
  );

  const onSelectionChange = useCallback(
    (key: Key | null) => {
      if (key) {
        takeSnapshot();
        const selectedActor = allActors.find((actor) => actor.label === key);

        if (!selectedActor) {
          return;
        }

        updateNodeAndSave({
          data: {
            actors: [selectedActor],
          },
        });

        setSearchTerm("");
        returnToMainToolbar();
      }
    },
    [returnToMainToolbar, allActors, takeSnapshot, updateNodeAndSave],
  );

  const onRemoveActor = useCallback(() => {
    takeSnapshot();

    updateNodeAndSave({
      data: {
        actors: undefined,
      },
    });
  }, [updateNodeAndSave, takeSnapshot]);

  return (
    <WrapperWithBackButton hasExtraTopPadding={hasActor}>
      {hasActor ? (
        <ul className={styles.selectedList}>
          {actors.map((actor) => (
            <li key={actor.label} className={styles.selectedItem}>
              <SelectedItem
                label={actor.label}
                icon={
                  <Icon
                    name="personCircleFill"
                    color={actor.color || "GrayWarm3"}
                    size={26}
                  />
                }
                clearSelection={onRemoveActor}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles.floatingTypeaheadWrapper}>
          <Typeahead
            label={ACTOR_TYPEAHEAD_LABEL}
            placeholder="Type or search people"
            ref={inputRef}
            onKeyDown={onKeyDown}
            onInputChange={onInputChange}
            onSelectionChange={onSelectionChange}
            classNamePopOver={styles.popover}
            size="medium"
          >
            {allActors.map((actor) => {
              return (
                <Typeahead.Item
                  key={actor.label}
                  id={actor.label}
                  textValue={actor.label}
                  className={styles.floatingTypeaheadItem}
                  size="medium"
                >
                  <Icon
                    name="personCircleFill"
                    color={actor.color || "GrayWarm3"}
                    size={26}
                  />
                  <div className={styles.truncateText}>
                    <Text type="SmallPrint2">{actor.label}</Text>
                  </div>
                </Typeahead.Item>
              );
            })}
          </Typeahead>
        </div>
      )}
    </WrapperWithBackButton>
  );
}
